import React, { useState } from 'react';
import {
  AccountBalance as AccountBalanceIcon,
  Badge as BadgeIcon,
  Block as BlockIcon,
  CameraAlt as CameraAltIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircle as CheckIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon,
  Error as ErrorIcon,
  Face as FaceIcon,
  Filter as FilterIcon,
  FlightTakeoff as PassportIcon,
  Lightbulb as LightbulbIcon,
  Mail as MailIcon,
  PersonOff as PersonOffIcon,
  PlayArrow as PlayIcon,
  QrCode as QrCodeIcon,
  RateReview as RateReviewIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  School as TutorialIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const Items = ({ usedNodes = [] as string[] }: { usedNodes?: string[] }) => {
  const [search, setSearch] = useState('');

  const onDragStart = (
    event: React.DragEvent,
    nodeType: string,
    parentNode: string,
    nodePosition: unknown,
  ) => {
    if (usedNodes.includes(nodeType)) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/reactflow/parent', parentNode);
    if (nodePosition) {
      event.dataTransfer.setData(
        'application/reactflow/position',
        JSON.stringify(nodePosition),
      );
    }
    event.dataTransfer.effectAllowed = 'move';
  };

  const nodeCategories = [
    {
      title: 'Flow Control',
      items: [{ name: 'Start', icon: <PlayIcon />, parentNode: '' }],
    },
    {
      title: 'Authentication',
      items: [
        { name: 'QR Code', icon: <QrCodeIcon />, parentNode: '' },
        {
          name: 'Terms & Conditions',
          icon: <DescriptionIcon />,
          parentNode: '',
        },
      ],
    },
    {
      title: 'Document Types',
      items: [
        {
          name: 'Identity Card',
          icon: <CreditCardIcon />,
          parentNode: '',
        },
        {
          name: 'Residence Permit',
          icon: <BadgeIcon />,
          parentNode: '',
        },
        {
          name: 'Passport',
          icon: <PassportIcon />,
          parentNode: '',
        },
        { name: 'Drivers License', icon: <CreditCardIcon />, parentNode: '' },
      ],
    },
    {
      title: 'Tutorials',
      items: [
        { name: 'ID Font Tutorial', icon: <TutorialIcon />, parentNode: '' },
        { name: 'ID Tutorial', icon: <TutorialIcon />, parentNode: '' },
        { name: 'Passport Tutorial', icon: <TutorialIcon />, parentNode: '' },
      ],
    },
    {
      title: 'Popup Screens',
      items: [
        { name: 'Document Expired', icon: <WarningIcon />, parentNode: '' },
        {
          name: 'Document Type Not Accepted',
          icon: <BlockIcon />,
          parentNode: '',
        },
        {
          name: 'Camera Permission Popup',
          icon: <CameraAltIcon />,
          parentNode: '',
        },
        { name: 'Unsupported Document', icon: <ErrorIcon />, parentNode: '' },
        { name: 'Underage', icon: <PersonOffIcon />, parentNode: '' },
        {
          name: 'Maximum Attempts Screen',
          icon: <BlockIcon />,
          parentNode: '',
        },
      ],
    },
    {
      title: 'Hard‑stops / Fraud',
      items: [
        { name: 'Fraud', icon: <SecurityIcon />, parentNode: '' },
        {
          name: 'Maximum Attempts Screen',
          icon: <BlockIcon />,
          parentNode: '',
        },
        { name: 'Underage', icon: <PersonOffIcon />, parentNode: '' },
        { name: 'Expired', icon: <ScheduleIcon />, parentNode: '' },
        { name: 'Unsupported', icon: <ErrorIcon />, parentNode: '' },
      ],
    },
    {
      title: 'Selfie',
      items: [{ name: 'Selfie', icon: <FaceIcon />, parentNode: '' }],
    },
    {
      title: 'Configs',
      items: [
        { name: 'Mask', icon: <FilterIcon />, parentNode: '' },
        { name: 'Lenses', icon: <CameraAltIcon />, parentNode: '' },
        {
          name: 'Proof of Identity',
          icon: <DescriptionIcon />,
          parentNode: '',
        },
      ],
    },
    {
      title: 'Documents',
      items: [
        { name: 'Payslip', icon: <ReceiptIcon />, parentNode: '' },
        { name: 'Utilities', icon: <LightbulbIcon />, parentNode: '' },
        {
          name: 'Bank Statement',
          icon: <AccountBalanceIcon />,
          parentNode: '',
        },
        { name: 'Official Letter', icon: <MailIcon />, parentNode: '' },
        { name: 'ID', icon: <BadgeIcon />, parentNode: '' },
      ],
    },
    {
      title: 'End Screens',
      items: [
        { name: 'Failed Verification', icon: <CancelIcon />, parentNode: '' },
        { name: 'Congrats', icon: <CheckCircleIcon />, parentNode: '' },
        {
          name: 'Manual Review Page',
          icon: <RateReviewIcon />,
          parentNode: '',
        },
      ],
    },
  ];

  // Enhanced filtering: if search matches category title, show all items in that category
  const filteredCategories = nodeCategories
    .map(category => {
      const titleMatch = category.title
        .toLowerCase()
        .includes(search.toLowerCase());
      return {
        ...category,
        items: titleMatch
          ? category.items
          : category.items.filter(item =>
              item.name.toLowerCase().includes(search.toLowerCase()),
            ),
      };
    })
    .filter(category => category.items.length > 0);

  return (
    <aside className="bg-white border-l border-[#E5E7EB] w-80 h-screen flex flex-col">
      <div className="flex-shrink-0 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <CategoryIcon className="text-[#6B6B6B]" />
          <h2 className="text-lg font-semibold text-[#0C2340]">
            Flow Components
          </h2>
        </div>

        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search components..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm"
          />
          <SearchIcon className="absolute top-2.5 right-3 text-[#5B6B80] text-sm" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 pt-0">
        <div className="space-y-6">
          {filteredCategories.map((category, idx) => (
            <div key={idx} className="space-y-3">
              <h3 className="text-sm font-medium text-[#252525] uppercase tracking-wide">
                {category.title}
              </h3>
              <div className="space-y-2">
                {category.items.map((item, itemIdx) => {
                  const isUsed = usedNodes.includes(item.name);
                  return (
                    <div
                      key={itemIdx}
                      className={`dndnode group ${
                        isUsed
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-grab active:cursor-grabbing'
                      }`}
                      onDragStart={e =>
                        onDragStart(e, item.name, item.parentNode, null)
                      }
                      draggable={!isUsed}
                    >
                      <div
                        className={`flex items-center space-x-3 p-3 rounded-lg border transition-all duration-200 ${
                          isUsed
                            ? 'bg-gray-100 border-[#E5E7EB]'
                            : 'bg-[#F7F9FC] border-[#E5E7EB] hover:bg-[#EAF0F9] hover:border-[#00387D]'
                        }`}
                      >
                        <div
                          className={
                            isUsed
                              ? 'text-[#5B6B80]'
                              : 'text-[#5B6B80] group-hover:text-blue-600'
                          }
                        >
                          {isUsed ? (
                            <CheckIcon className="text-green-500" />
                          ) : (
                            item.icon
                          )}
                        </div>
                        <span
                          className={
                            isUsed
                              ? 'text-[#5B6B80]'
                              : 'text-[#252525] group-hover:text-blue-700'
                          }
                        >
                          {item.name}
                        </span>
                        {isUsed && (
                          <span className="ml-auto text-xs text-green-600 font-medium">
                            Added
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default Items;
